/**
 * Иконки интерфейса: чиби-атлас `ui-icons` (ISSUE-114), без него — SVG-заглушки и пиксельные иконки.
 *
 * Точка замены — {@link setIconRenderer}: A5/I1 регистрирует функцию, которая рисует иконку из атласа
 * (например, `<span>` с `background-image` и позицией кадра). Вернула `null` — остаётся заглушка.
 * Имена иконок — {@link IconName}; новые добавляются сюда (и в атлас A5 под тем же именем).
 */
import type { ComponentChildren, JSX } from "preact";
import { PIXEL_ICON_SIZE, pixelIcon, type PixelIconName } from "./pixel-icons";

export const ICON_NAMES = [
  "coin",
  "skull",
  "tower",
  "lightning",
  "gear",
  "tasks",
  "streak",
  "chest",
  "friends",
  "rank",
  "gunner",
  "bomber",
  "sparky",
  "blaze",
  "frosty",
] as const;
type PlaceholderName = (typeof ICON_NAMES)[number];
/** Заглушки + воксельные иконки A5 (`coin`, `skill_*`, `tab_*`, id усилений) — см. pixel-icons.ts. */
/** Портреты премиум-героев беты: из атласа `hero-portraits` (tools/ui-art/portraits.mjs), заглушки нет. */
export const HERO_PORTRAIT_NAMES = ["hero_radiation", "hero_cryo", "hero_laser", "hero_plague"] as const;
export type IconName = PlaceholderName | PixelIconName | (typeof HERO_PORTRAIT_NAMES)[number];

export type IconRenderer = (name: IconName, size: number) => JSX.Element | null;

let renderer: IconRenderer | null = null;

/** Подключить настоящие иконки (атлас A5). `null` — вернуть заглушки. */
export function setIconRenderer(r: IconRenderer | null): void {
  renderer = r;
}

const svg = (children: ComponentChildren) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {children}
  </svg>
);

/** Заглушки: плоские фигуры с тёмной обводкой, читаются на тёмной плашке. */
const PLACEHOLDERS: Record<PlaceholderName, () => JSX.Element> = {
  coin: () =>
    svg(
      <>
        <circle cx="12" cy="12" r="10" fill="#f6b922" stroke="#7a4d05" stroke-width="1.5" />
        <circle cx="12" cy="12" r="6.5" fill="#ffd54a" stroke="#c98a0c" stroke-width="1.5" />
        <rect x="10.5" y="8" width="3" height="8" rx="1" fill="#c98a0c" />
      </>,
    ),
  skull: () =>
    svg(
      <>
        <path d="M4 10a8 8 0 0 1 16 0v4h-2v4H6v-4H4z" fill="#f3f4f6" stroke="#1f2937" stroke-width="1.5" />
        <rect x="7" y="9" width="3.5" height="3.5" fill="#111827" />
        <rect x="13.5" y="9" width="3.5" height="3.5" fill="#111827" />
        <path d="M9 18v-2.5M12 18v-2.5M15 18v-2.5" stroke="#111827" stroke-width="1.5" />
      </>,
    ),
  tower: () =>
    svg(
      <>
        <path d="M5 21V8h2V5h2v3h2V5h2v3h2V5h2v3h2v13z" fill="#9aa3ae" stroke="#374151" stroke-width="1.5" />
        <rect x="10" y="15" width="4" height="6" fill="#4b5563" />
        <path d="M12 1v4" stroke="#374151" stroke-width="1.5" />
        <path d="M12 1h4l-1 1.5 1 1.5h-4" fill="#2f8cff" />
      </>,
    ),
  lightning: () => svg(<path d="M13 2 4 14h6l-2 8 10-12h-6l2-8z" fill="#e8f6ff" stroke="#1b56b8" stroke-width="1.5" />),
  gear: () =>
    svg(
      <>
        <path
          d="M10 2h4l.6 3 2.4 1.4 2.9-1 2 3.4-2.3 2v2.4l2.3 2-2 3.4-2.9-1-2.4 1.4-.6 3h-4l-.6-3L7 17.2l-2.9 1-2-3.4 2.3-2v-2.4l-2.3-2 2-3.4 2.9 1L9.4 5z"
          fill="#cbd5e1"
          stroke="#334155"
          stroke-width="1.2"
        />
        <circle cx="12" cy="12" r="3" fill="#334155" />
      </>,
    ),
  tasks: () =>
    svg(
      <>
        <rect x="4" y="3" width="16" height="19" rx="2" fill="#f5d7a1" stroke="#7c4a12" stroke-width="1.5" />
        <rect x="8" y="1.5" width="8" height="4" rx="1" fill="#b45309" />
        <path d="M7 10h10M7 14h10M7 18h6" stroke="#7c4a12" stroke-width="1.5" />
      </>,
    ),
  streak: () =>
    svg(
      <>
        <rect x="3" y="4" width="18" height="17" rx="2" fill="#fff" stroke="#374151" stroke-width="1.5" />
        <path d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3H3z" fill="#e8323a" />
        <path d="m8 14 3 3 5-6" fill="none" stroke="#1e7d2c" stroke-width="2.2" />
      </>,
    ),
  chest: () =>
    svg(
      <>
        <path d="M3 10a6 6 0 0 1 6-6h6a6 6 0 0 1 6 6z" fill="#d97706" stroke="#78350f" stroke-width="1.5" />
        <rect x="3" y="10" width="18" height="10" fill="#b45309" stroke="#78350f" stroke-width="1.5" />
        <rect x="10" y="8.5" width="4" height="5" rx="1" fill="#fcd34d" stroke="#78350f" stroke-width="1" />
      </>,
    ),
  friends: () =>
    svg(
      <>
        <circle cx="8" cy="8" r="4" fill="#60a5fa" stroke="#1e3a8a" stroke-width="1.2" />
        <path d="M1 21a7 7 0 0 1 14 0z" fill="#3b82f6" stroke="#1e3a8a" stroke-width="1.2" />
        <circle cx="16.5" cy="8" r="3.5" fill="#fdba74" stroke="#7c2d12" stroke-width="1.2" />
        <path d="M11 21a6 6 0 0 1 12 0z" fill="#f97316" stroke="#7c2d12" stroke-width="1.2" />
      </>,
    ),
  rank: () =>
    svg(
      <>
        <path d="M6 3h12v5a6 6 0 0 1-12 0z" fill="#f6b922" stroke="#7a4d05" stroke-width="1.5" />
        <path d="M6 5H3v2a4 4 0 0 0 4 4M18 5h3v2a4 4 0 0 1-4 4" fill="none" stroke="#7a4d05" stroke-width="1.5" />
        <path d="M10 14h4v3h3v4H7v-4h3z" fill="#d18e0c" stroke="#7a4d05" stroke-width="1.5" />
      </>,
    ),
  gunner: () => hero("#2f6fd6", "#1e3a8a", <path d="M7 15h7v-2h3v4H7z" fill="#374151" />),
  bomber: () => hero("#e0742a", "#7c2d12", <circle cx="15" cy="16" r="3" fill="#1f2937" />),
  sparky: () => hero("#f4d03f", "#7a5c05", <path d="m13 12-3 4h3l-1 3 3-4h-3z" fill="#2f8cff" />),
  blaze: () =>
    hero(
      "#e23d32",
      "#7f1d1d",
      <path d="M12 19c-3 0-4-3-2-6 0 2 1 2 1 2 0-3 2-4 2-6 2 2 4 4 2 8-.5 1-1.5 2-3 2z" fill="#fbbf24" />,
    ),
  frosty: () =>
    hero("#7dd3fc", "#0c4a6e", <path d="M12 12v8M8.5 14l7 4M15.5 14l-7 4" stroke="#fff" stroke-width="1.6" />),
};

/** Портрет-заглушка героя: голова-куб в каске цвета героя + знак навыка. */
function hero(color: string, dark: string, sign: JSX.Element) {
  return svg(
    <>
      <rect x="2" y="2" width="20" height="20" rx="4" fill={color} stroke={dark} stroke-width="1.5" />
      <rect x="6" y="6" width="12" height="9" rx="1" fill="#f1c7a0" stroke={dark} stroke-width="1" />
      <path d="M5 7a7 4 0 0 1 14 0z" fill={dark} />
      <rect x="8.5" y="9" width="2" height="2" fill="#1f2937" />
      <rect x="13.5" y="9" width="2" height="2" fill="#1f2937" />
      {sign}
    </>,
  );
}

/** Иконка подключённого атласа (см. {@link setIconRenderer}) или `null`, если её там нет. */
export function customIcon(name: string, size: number): JSX.Element | null {
  return renderer?.(name as IconName, size) ?? null;
}

export interface IconProps {
  name: IconName;
  /** Сторона, px. */
  size?: number;
  class?: string;
}

/** Воксельная иконка A5 как SVG из прямоугольников; `null` — такой нет (остаётся заглушка). */
export function pixel(name: string): JSX.Element | null {
  const runs = pixelIcon(name);
  if (!runs) return null;
  return (
    <svg
      viewBox={`0 0 ${PIXEL_ICON_SIZE} ${PIXEL_ICON_SIZE}`}
      shape-rendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      {runs.map((r) => (
        <path d={r.d} fill={r.color} />
      ))}
    </svg>
  );
}

/** Иконка: из атласа, если подключён (см. {@link setIconRenderer}), иначе SVG-заглушка. */
export function Icon({ name, size = 24, class: cls }: IconProps) {
  const custom = renderer?.(name, size) ?? null;
  return (
    <span
      class={cls ? `kit-icon ${cls}` : "kit-icon"}
      data-icon={name}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      {custom ?? pixel(name) ?? PLACEHOLDERS[name as PlaceholderName]?.() ?? null}
    </span>
  );
}
